import React, { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { debounce, get } from 'lodash';
import useFormValues from '../../common/hook/queries/useFormValues';
import { useFetchProductListQuery } from '../../../../../hooks/product/useFetchProductListQuery';
import { useAccountUserListQuery } from '../../../../account/user/list/AccountUserList/queries/useAccountUserListQuery';
import { useReviewCriteriaQuery } from '../../common/hook/queries/useReviewCriteriaQuery';

import Panel from '../../../../../components/outline/Panel';
import SelectField from '../../../../../components/common/SelectField';
import Rate from '../../../../../components/utils/Rate/Rate';
import SearchBar from '../../../../../components/common/SearchBar';
import FormTextArea from '../../../../../components/common/FormTextArea';
import NewImgUpload from '../../../../../components/NewImgUpload';
import FormField from '../../../../../components/common/FormField';
import BasicLabel from '../../../../../components/common/BasicLabel';
import LabelWithCount from '../../../../../components/common/LableWithCount';
import ReviewCriteriaField from '../../components/ReviewCriteriaField';
import { countCharactersExcludeNewlines } from '../../../../../lib/utils/countCharacters';

import {
  CS_REVIEW_CONTENT_LIMIT,
  CS_REVIEW_USER_DEFAULT_ID,
  MAXIMUM_NUMBER_OF_REVIEW_IMG,
} from '../../../../../constants/service/app/csReviewConstants';
import {
  BRAND_SEARCH_PARAMS_MAP,
  PRODUCT_LIST_SEARCH_KEYWORD_TYPE,
  PRODUCT_LIST_SEARCH_KEYWORD_TYPE_SELECT_LIST,
} from '../../../../../constants/service/app/productConstants';
import {
  USER_LIST_SEARCH_KEYWORD_TYPE_SELECT_LIST,
  USER_LIST_SEARCH_PARAMS_MAP,
} from '../../../../../constants/service/app/userConstants';
import { ACCOUNT_USER_LIST_STATUS } from '../../../../../constants/service/app/accountUserConstants';

import './CsReviewCreateForm.scss';
import { BRAND_STATUS_V2 } from '../../../../../types/brand/common';
import { PRODUCT_STATUS_V2 } from '../../../../../types/product';
import { numberStringRegExp } from '../../../../../regExp/numberStringRegExp';
import toastMsg from '../../../../../lib/toastMsg';

const CsReviewCreateForm = () => {
  const { control, setValue } = useFormContext();
  const { userSearchType, selectedUser, selectedProduct, content, imgUrls } = useFormValues();
  const characterCount = countCharactersExcludeNewlines(content);

  const productSearchType = useWatch({ control, name: 'productSearchType' });

  const [product, setProduct] = useState(null);
  const [user, setUser] = useState(CS_REVIEW_USER_DEFAULT_ID);

  const categoryId = get(selectedProduct, 'mainCategories[0].id', null);

  const { data } = useFetchProductListQuery({
    payload: {
      [BRAND_SEARCH_PARAMS_MAP[productSearchType]]: product,
      brandStatus: BRAND_STATUS_V2.ACTIVE,
      status: [PRODUCT_STATUS_V2.NOW_ON_SALE, PRODUCT_STATUS_V2.SALES_READY].join(','),
    },
    options: {
      enabled:
        (productSearchType === PRODUCT_LIST_SEARCH_KEYWORD_TYPE.PRODUCT_NAME && !!product) ||
        (productSearchType === PRODUCT_LIST_SEARCH_KEYWORD_TYPE.PRODUCT_ID && numberStringRegExp.test(product)),
    },
  });

  const { data: criteriaInfo, isSuccess: isCriteriaInfoFetchSuccess } = useReviewCriteriaQuery({
    id: categoryId,
  });

  const { data: users } = useAccountUserListQuery(
    {
      [USER_LIST_SEARCH_PARAMS_MAP[userSearchType]]: user,
      status: ACCOUNT_USER_LIST_STATUS.ACTIVATE,
    },
    {
      enabled: !!user,
      select: (data) => {
        return data.users;
      },
    },
  );

  const handleSearchProduct = debounce((value) => {
    setProduct(value);

    if (productSearchType === PRODUCT_LIST_SEARCH_KEYWORD_TYPE.PRODUCT_ID && !numberStringRegExp.test(value)) {
      toastMsg.error('상품 ID는 숫자만 입력 가능합니다.');
    }
  }, 500);

  const handleSearchUser = debounce((value) => {
    setUser(value);
  }, 500);

  useEffect(() => {
    if (criteriaInfo && !criteriaInfo?.reviewCriteria.length) {
      setValue('reviewCriteria', []);
    }
  }, [criteriaInfo, isCriteriaInfoFetchSuccess, setValue]);

  return (
    <Panel className="cs-review-create-info">
      <div className="panel-header">
        <div className="title">후기 정보</div>
      </div>
      <section className="panel-body">
        <div className="review-info-wrap">
          <div className="review-info">
            <FormField
              label={
                <SelectField
                  name="productSearchType"
                  options={PRODUCT_LIST_SEARCH_KEYWORD_TYPE_SELECT_LIST}
                  isRequired={true}
                  editable={true}
                />
              }
            >
              <SearchBar name="selectedProduct" onHandleSearch={handleSearchProduct} list={data?.products} />
            </FormField>
            {selectedProduct && (
              <FormField label={<BasicLabel label={'상품 ID'} isRequired={false} editable={true} />}>
                <div className="info-wrap">
                  <a href={`/product/detail/${selectedProduct.id}`} target="_blank" rel="noreferrer">
                    {selectedProduct.id}
                  </a>
                </div>
              </FormField>
            )}
            <FormField
              label={
                <SelectField
                  name="userSearchType"
                  options={USER_LIST_SEARCH_KEYWORD_TYPE_SELECT_LIST}
                  isRequired={true}
                  editable={true}
                />
              }
            >
              <SearchBar
                name="selectedUser"
                onHandleSearch={handleSearchUser}
                list={users}
                defaultUserId={CS_REVIEW_USER_DEFAULT_ID}
              />
            </FormField>
            {selectedUser && (
              <FormField label={<BasicLabel label={'유저 식별 ID'} isRequired={false} editable={true} />}>
                <div className="info-wrap">
                  <a href={`/account/user/detail/${selectedUser?.id}`} target="_blank" rel="noreferrer">
                    {selectedUser?.id}
                  </a>
                </div>
              </FormField>
            )}
            <FormField label={<BasicLabel label={'평점'} isRequired={true} editable={true} />}>
              <Controller
                name="rate"
                control={control}
                defaultValue={5}
                rules={{ required: true }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <div className="content-wrap">
                    <Rate allowClear={false} value={Number(value)} name="rate" onChange={onChange} />
                    {<p className="error-message">{error ? error.message : null}</p>}
                  </div>
                )}
              />
            </FormField>
            {criteriaInfo && criteriaInfo?.reviewCriteria.length > 0 && (
              <FormField label={<BasicLabel label={'평가 항목'} isRequired={true} editable={true} />}>
                <Controller
                  name="reviewCriteria"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <div className="content-wrap">
                      <ReviewCriteriaField list={criteriaInfo.reviewCriteria} value={value} onChange={onChange} />
                      {<p className="error-message">{error ? error.message : null}</p>}
                    </div>
                  )}
                />
              </FormField>
            )}
            <FormField
              label={
                <LabelWithCount
                  label={'구매평'}
                  isRequired={true}
                  baseNumber={CS_REVIEW_CONTENT_LIMIT}
                  count={characterCount || 0}
                />
              }
            >
              <FormTextArea
                id="content"
                type="text"
                name="content"
                label="구매평"
                placeholder="구매평"
                editable={true}
                limit={CS_REVIEW_CONTENT_LIMIT}
              />
            </FormField>
            <FormField
              label={
                <LabelWithCount
                  label={'상품 후기 이미지'}
                  isRequired={false}
                  baseNumber={MAXIMUM_NUMBER_OF_REVIEW_IMG}
                  count={imgUrls?.length || 0}
                />
              }
            >
              <Controller
                name="imgUrls"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <div className="content-wrap">
                    <NewImgUpload
                      name="imgUrls"
                      modalTitle="상품 후기 이미지"
                      maxCnt={MAXIMUM_NUMBER_OF_REVIEW_IMG}
                      onChange={onChange}
                      value={value}
                    />
                  </div>
                )}
              />
            </FormField>
          </div>
        </div>
      </section>
    </Panel>
  );
};

export default CsReviewCreateForm;
